import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { METRIC_UNITS } from "../../../constants/metricCategories";
import MetricSelector from "./MetricSelector";
import ParticipantCoverageFilters, {
  DatePreset,
} from "./ParticipantCoverageFilters";
import MetricCoverageCalendar from "./MetricCoverageCalendar";
import AggregationSummaryTable from "./AggregationSummaryTable";
import {
  DataCoverageService,
  type MetricStatsResponse,
} from "../../../services/dataCoverageService";
import { apiService } from "../../../config/api";
import {
  AggregationType,
  isValidAggregationRange,
  AggregationPeriod,
} from "../../../utils/aggregationUtils";

interface MetricStatsCardProps {
  participantId: string;
  deviceType: "fitbit" | "healthkit" | "both" | "none";
  selectedParticipant: { firstName?: string; lastName?: string } | null;
  onStatsChange?: (stats: MetricStatsResult[]) => void;
}

interface MetricStatsResult {
  metric: {
    key: string;
    label: string;
  };
  range: {
    fromDate: string;
    endDate: string;
    totalDays: number;
  };
  statistics: {
    minValue: number | null;
    maxValue: number | null;
    meanValue: number | null;
    daysWithData: number;
    daysWithoutData: number;
    coveragePct: number;
    missingDates: string[];
  };
  aggregation: {
    type: AggregationType;
    periods: AggregationPeriod[];
  };
  fetch?: {
    source: string;
    statusMessage: string;
    refreshedFromApi: boolean;
    failedFitbitRefreshRanges?: {
      startDate: string;
      endDate: string;
      dayCount: number;
      statusMessage: string;
    }[];
  };
}

const MetricStatsCard: React.FC<MetricStatsCardProps> = ({
  participantId,
  deviceType,
  selectedParticipant,
  onStatsChange,
}) => {
  const [selectedMetric, setSelectedMetric] = React.useState<string | null>(
    null,
  );
  const [fromDate, setFromDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [preset, setPreset] = React.useState<DatePreset | null>("7d");
  const [aggregationType, setAggregationType] =
    React.useState<AggregationType>("none");
  const [aggregationPeriods, setAggregationPeriods] = React.useState<
    AggregationPeriod[]
  >([]);
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState<MetricStatsResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [openMissingDates, setOpenMissingDates] = React.useState(false);
  const [copiedSnackbar, setCopiedSnackbar] = React.useState(false);

  React.useEffect(() => {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    setFromDate(start);
    setEndDate(end);
  }, []);

  React.useEffect(() => {
    setSelectedMetric(null);
    setResult(null);
    setError(null);
    setAggregationPeriods([]);
    setAggregationType("none");
  }, [participantId]);

  // Notify parent when a metric result is fetched
  React.useEffect(() => {
    if (result && onStatsChange) {
      // Convert MetricStatsResult to MetricStatsResponse format
      const metricsResponse: MetricStatsResponse = {
        metric: result.metric,
        range: result.range,
        statistics: result.statistics,
        aggregation: result.aggregation,
        fetch: result.fetch,
      };
      // Pass as an array with single result
      onStatsChange([metricsResponse]);
    }
  }, [result, onStatsChange]);

  const applyPreset = (nextPreset: DatePreset | null) => {
    setPreset(nextPreset);
    if (!nextPreset) return;

    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    let start = new Date(today);

    if (nextPreset === "7d") {
      start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
    } else if (nextPreset === "1m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 1);
      start.setDate(start.getDate() + 1);
    } else if (nextPreset === "6m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 6);
      start.setDate(start.getDate() + 1);
    } else {
      start = new Date(today);
      start.setFullYear(start.getFullYear() - 1);
      start.setDate(start.getDate() + 1);
    }

    setFromDate(start.toISOString().slice(0, 10));
    setEndDate(end);
  };

  const handleRun = async () => {
    if (!selectedMetric || !fromDate || !endDate) {
      setError("Please select a metric and date range.");
      return;
    }

    if (
      aggregationType !== "none" &&
      !isValidAggregationRange(fromDate, endDate, aggregationType)
    ) {
      setError(`Date range too small for ${aggregationType} aggregation.`);
      return;
    }

    try {
      setRunning(true);
      setError(null);
      setResult(null);

      const token = await apiService.getAuthToken();
      if (!token) {
        setError("Sign in to fetch metric stats.");
        return;
      }

      const response = await DataCoverageService.getMetricStats(
        participantId,
        {
          metricKey: selectedMetric,
          fromDate,
          endDate,
          aggregationType,
          refreshIfMissing: true,
        },
        token,
      );

      if (response.success && (response as any).data) {
        const responseData = (response as any).data;
        setResult(responseData);
        setAggregationPeriods(responseData.aggregation?.periods || []);
      } else {
        setError("Failed to fetch metric stats.");
      }
    } catch (err: any) {
      setError(err?.message || "Error fetching metric stats.");
    } finally {
      setRunning(false);
    }
  };

  const handleCopyMissingDates = () => {
    if (!result) return;
    const textToCopy = result.statistics.missingDates.join("\n");
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopiedSnackbar(true);
    });
  };

  const handleAggregationTypeChange = (value: string) => {
    const nextType = value as AggregationType;
    setAggregationType(nextType);
    setAggregationPeriods([]);
  };

  const unit = selectedMetric
    ? METRIC_UNITS[selectedMetric as keyof typeof METRIC_UNITS] || ""
    : "";

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Metric Statistics
        </Typography>

        {/* Controls */}
        <Stack spacing={2}>
          <MetricSelector
            value={selectedMetric}
            onChange={setSelectedMetric}
            deviceType={deviceType}
            disabled={!selectedParticipant}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <ParticipantCoverageFilters
              disabled={!selectedParticipant || !selectedMetric}
              fromDate={fromDate}
              endDate={endDate}
              preset={preset}
              onFromDateChange={(value) => {
                setPreset(null);
                setFromDate(value);
              }}
              onEndDateChange={(value) => {
                setPreset(null);
                setEndDate(value);
              }}
              onPresetChange={applyPreset}
              onRun={handleRun}
              running={running}
              showAggregation
              aggregationType={aggregationType}
              onAggregationTypeChange={handleAggregationTypeChange}
            />
          </Box>
        </Stack>

        {/* Loading */}
        {running && <LinearProgress />}

        {/* Error */}
        {error && <Alert severity="error">{error}</Alert>}

        {result?.fetch?.statusMessage && (
          <Alert severity="info">{result.fetch.statusMessage}</Alert>
        )}

        {result?.fetch?.failedFitbitRefreshRanges &&
          result.fetch.failedFitbitRefreshRanges.length > 0 && (
            <Alert severity="warning">
              Fitbit data could not be retrieved for:{" "}
              {result.fetch.failedFitbitRefreshRanges
                .map((range) => `${range.startDate} to ${range.endDate}`)
                .join(", ")}
            </Alert>
          )}

        {/* Results */}
        {result && !running && (
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {result.metric.label} ({result.range.fromDate} to{" "}
              {result.range.endDate})
            </Typography>

            <Grid container spacing={2}>
              {/* Min Value */}
              {result.statistics.minValue !== null && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Min Value
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                      {result.statistics.minValue.toFixed(2)} {unit}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Max Value */}
              {result.statistics.maxValue !== null && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Max Value
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                      {result.statistics.maxValue.toFixed(2)} {unit}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Mean Value */}
              {result.statistics.meanValue !== null && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Mean Value
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                      {result.statistics.meanValue.toFixed(2)} {unit}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Days with Data */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, textAlign: "center", backgroundColor: "#e8f5e9" }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Days with Data
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                    {result.statistics.daysWithData}
                  </Typography>
                </Paper>
              </Grid>

              {/* Days without Data */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, textAlign: "center", backgroundColor: "#ffebee" }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Days without Data
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                    {result.statistics.daysWithoutData}
                  </Typography>
                </Paper>
              </Grid>

              {/* Coverage */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, textAlign: "center", backgroundColor: "#e3f2fd" }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Coverage
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                    {result.statistics.coveragePct.toFixed(1)}%
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Coverage Calendar */}
            <MetricCoverageCalendar
              fromDate={result.range.fromDate}
              endDate={result.range.endDate}
              missingDates={result.statistics.missingDates}
            />

            {/* Aggregation Summary Table */}
            {aggregationType !== "none" && aggregationPeriods.length > 0 && (
              <AggregationSummaryTable
                periods={aggregationPeriods}
                metricUnit={unit}
                aggregationType={
                  aggregationType as "weekly" | "monthly" | "yearly"
                }
              />
            )}

            {/* View Missing Dates Button */}
            {result.statistics.missingDates.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setOpenMissingDates(true)}
              >
                View Missing Days ({result.statistics.missingDates.length})
              </Button>
            )}
          </Stack>
        )}

        {/* Missing Dates Dialog */}
        <Dialog
          open={openMissingDates}
          onClose={() => setOpenMissingDates(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Missing Days: {result?.metric.label}</span>
            <IconButton
              aria-label="close"
              onClick={() => setOpenMissingDates(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {result?.statistics.missingDates.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No missing days found.
              </Typography>
            ) : (
              <Stack spacing={0.75}>
                {result?.statistics.missingDates.map((day) => (
                  <Typography key={day} variant="body2">
                    {day}
                  </Typography>
                ))}
                <Box
                  sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCopyMissingDates}
                  >
                    Copy List
                  </Button>
                </Box>
              </Stack>
            )}
          </DialogContent>
        </Dialog>

        {/* Copy Snackbar */}
        <Snackbar
          open={copiedSnackbar}
          autoHideDuration={2000}
          onClose={() => setCopiedSnackbar(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={() => setCopiedSnackbar(false)} severity="success">
            Copied to clipboard
          </Alert>
        </Snackbar>
      </Stack>
    </Paper>
  );
};

export default MetricStatsCard;
